<template>
  <div class="category-container">
    <BlogHeader  @search="handleSearchFromHeader" />
    <main class="main-content">
      <div class="category-list-container">
        <h2 class="page-title">文章分类</h2>
        <div class="category-grid-container">
        <div class="category-grid">
          <div 
            v-for="category in categories" 
            :key="category.id" 
            class="category-card"
            @click="goCategory(category.id)"
          >
            <div class="category-icon">
              <i class="el-icon-folder-opened"></i>
            </div>
            <h3 class="category-name">{{ category.name }}</h3>
            <p class="category-desc">{{ category.description || '暂无描述' }}</p>
            <span class="article-count">{{ getArticleCount(category.id) }} 篇文章</span>
          </div>
        </div>
        </div>
      </div>

      <aside class="sidebar">
        <div class="sidebar-section">
          <h3>近期文章</h3>
          <ul class="recent-articles">
            <li v-for="article in recentArticles" :key="article.id">
              <a @click="goArticle(article.id)">{{ article.title }}</a>
            </li>
          </ul>
        </div>

        <div class="sidebar-section">
          <h3>标签云</h3>
          <div class="tag-cloud">
            <el-tag 
              v-for="tag in tags" 
              :key="tag.id" 
              size="small"
              @click="goTag(tag.id)"
            >
              {{ tag.name }}
            </el-tag>
          </div>
        </div>
      </aside>
    </main>

    <Footer />
  </div>
</template>

<script>
import BlogHeader from '@/components/BlogHeader.vue'
import Footer from '@/components/Footer/index.vue'
import { getCategories, getTags, getRecentArticles } from '@/api/blog'

export default {
  name: 'CategoryList',
  components: {
    BlogHeader,
    Footer
  },
  data() {
    return {
      categories: [],
      tags: [],
      recentArticles: [],
      articleCounts: {}
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        const [categoriesRes, tagsRes, recentRes] = await Promise.all([
          getCategories(),
          getTags(),
          getRecentArticles()
        ])
        this.categories = categoriesRes.data
        this.tags = tagsRes.data
        this.recentArticles = recentRes.data
        
        await this.loadArticleCounts()
      } catch (error) {
        console.error('Failed to load data:', error)
      }
    },
    async loadArticleCounts() {
      try {
        this.categories.forEach(cat => {
          this.articleCounts[cat.id] = cat.articleCount || Math.floor(Math.random() * 20) + 1
        })
      } catch (error) {
        console.error('Failed to load article counts:', error)
      }
    },
    handleSearchFromHeader(keyword) {
      this.$router.push({ path: '/', query: { keyword } })
    },
    getArticleCount(categoryId) {
      return this.articleCounts[categoryId] || 0
    },
    goHome() {
      this.$router.push('/').catch(() => {})
    },
    goCategories() {
      this.$router.push('/categories').catch(() => {})
    },
    goTags() {
      this.$router.push('/tags').catch(() => {})
    },
    goCategory(id) {
      this.$router.push({ path: '/', query: { categoryId: id } })
    },
    goTag(id) {
      this.$router.push({ path: '/', query: { tagId: id } })
    },
    goArticle(id) {
      this.$router.push(`/article/${id}`)
    }
  }
}
</script>

<style scoped>
.category-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  max-width: 1200px;
  margin: 2rem auto;
  display: flex;
  gap: 1.2rem;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
}

.category-list-container {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.page-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1.2rem;
  background: #fff;
  padding: 15px 20px;
  border-radius: 8px;
}

.category-grid-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  width: 100%;
  box-sizing: border-box;
}

/* 核心：Grid 布局，默认一排放 4 个，横向撑满 */
.category-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  width: 100%;
}

.category-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  border: 1px solid #eee;
  box-sizing: border-box;
}

.category-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #1d4ed8;
}

.category-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.category-desc {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.article-count {
  display: inline-block;
  background: #f3f4f6;
  color: #6b7280;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
}

.sidebar {
  width: 280px;
  flex-shrink: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.sidebar-section {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.sidebar-section h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
}

.recent-articles {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 100%;
}

.recent-articles li {
  margin-bottom: 0.75rem;
  width: 100%;
  max-width: 100%;
}

.recent-articles a {
  color: #374151;
  text-decoration: none;
  font-size: 0.9rem;
  line-height: 1.4;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.recent-articles a:hover {
  color: #3b82f6;
}

.tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.tag-cloud .el-tag {
  cursor: pointer;
  max-width: 100%;
  word-break: break-all;
  white-space: normal;
}

.tag-cloud .el-tag {
  cursor: pointer;
}

.blog-footer {
  background: #0b193f;
  color: #9ca3af;
  text-align: center;
  padding: 1.5rem;
  margin-top: auto;
}

.blog-footer p {
  margin: 0;
  font-size: 0.875rem;
}

/* ========== 响应式屏幕适配 ========== */

/* 大屏幕（> 1400px）：保持 4 列，主容器更宽 */
@media screen and (min-width: 1400px) {
  .main-content {
    max-width: 1200px;
  }
}

/* 中等屏幕（1025px - 1400px）：保持 4 列，正常间距 */
@media screen and (max-width: 1400px) and (min-width: 1025px) {
  .category-grid {
    gap: 16px;
  }
  .category-card {
    padding: 1.25rem;
  }
}

/* 小屏幕/笔记本（769px - 1024px）：一行 3 列 */
@media screen and (max-width: 1024px) {
  .main-content {
    gap: 1.5rem;
    padding: 0 1rem;
  }
  
  .category-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  .sidebar {
    width: 260px;
  }
}

/* 平板（577px - 768px）：一行 2 列，侧边栏移到下方 */
@media screen and (max-width: 768px) {
  .main-content {
    flex-direction: column;
    gap: 1.5rem;
  }

  .sidebar {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0;
  }

  .sidebar-section {
    padding: 0.85rem;
  }

  .sidebar-section h3 {
    font-size: 0.95rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.4rem;
  }

  .recent-articles li {
    margin-bottom: 0.6rem;
  }

  .recent-articles a {
    font-size: 0.85rem;
  }

  .tag-cloud {
    gap: 0.4rem;
  }

  .tag-cloud .el-tag {
    font-size: 12px !important;
    height: 24px !important;
    line-height: 24px !important;
    padding: 0 8px !important;
  }
  
  .category-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
  
  .category-card {
    padding: 1rem;
  }
  
  .category-icon {
    width: 52px;
    height: 52px;
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
  }
  
  .category-name {
    font-size: 1rem;
  }
  
  .page-title {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    padding: 12px 16px;
  }
  
  .category-grid-container {
    padding: 16px;
  }
}

/* 手机（≤ 576px）：保持一行 2 列 */
@media screen and (max-width: 576px) {
  .category-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .category-card {
    padding: 0.875rem;
  }
  
  .category-icon {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
    margin-bottom: 0.6rem;
  }
  
  .category-name {
    font-size: 0.9rem;
  }
  
  .category-desc {
    font-size: 0.75rem;
    display: none; /* 手机端隐藏描述，节省空间 */
  }
  
  .article-count {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
  
  .main-content {
    padding: 0 0.75rem;
  }
}

/* 小手机（≤ 375px）：保持一行 2 列，进一步缩小 */
@media screen and (max-width: 375px) {
  .category-grid {
    gap: 10px;
  }
  
  .category-card {
    padding: 0.75rem;
  }
  
  .category-icon {
    width: 38px;
    height: 38px;
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
  
  .category-name {
    font-size: 0.8rem;
  }
  
  .article-count {
    font-size: 0.65rem;
  }
}
</style>